import {
  Button,
  Checkbox,
  Divider,
  Input,
  InputNumber,
  message,
  Select,
  Space,
  Typography,
} from "antd";
import { useEffect, useRef, useState } from "react";
import { uploadImage } from "../../../apis/misc.api";
import { attachDocumentToPO } from "../../../apis/po.api";
import {
  fetchPIfromId,
  getEstimateAdditionalChargeRefundRaisedFor,
  getEstimateItemPORaisedFor,
  raiseRefundPOForEntireEstimate,
} from "../../../apis/pro_forma_invoice.api";
import { getImageUrl } from "../../../helpers/image_helper";
import { ApprovalReasons } from "../../../pages/refunds-v2";

export interface PartialOrderRefundProps {
  estimateId: number;
}

export function PartialOrderRefund({ estimateId }: PartialOrderRefundProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [itemsPoRaisedFor, setItemsPoRaisedFor] = useState<any[]>([]);
  const [
    additionalChargesRefundRaisedFor,
    setAdditionalChargesRefundRaisedFor,
  ] = useState<any[]>([]);
  const [estimateItems, setEstimateItems] = useState<any[]>([]);
  const [additionalCharges, setAdditionalCharges] = useState<any[]>([]);

  const [checkedItems, setCheckedItems] = useState<number[]>([]);
  const [itemQuantityMap, setItemQuantityMap] = useState<
    Record<number, number>
  >({});
  const [checkedAdditionalCharges, setCheckedAdditionalCharges] = useState<
    number[]
  >([]);
  const [attachmentUrls, setAttachmentUrls] = useState<string[]>([]);

  const [approvalReason, setApprovalReason] = useState<string | null>(null);
  const [approvalRemark, setApprovalRemark] = useState<string>();
  const [showApprovalRemark, setShowApprovalRemark] = useState(false);

  useEffect(() => {
    if (approvalReason === "Others") {
      setShowApprovalRemark(true);
    } else {
      setShowApprovalRemark(false);
    }
  }, [approvalReason]);

  const itemsThatCanBeRefunded = estimateItems.filter((estimateItem) => {
    const itemPoRaisedFor = itemsPoRaisedFor.find(
      (item) => item.estimate_item_id === estimateItem.id
    );
    if (itemPoRaisedFor) {
      return (
        Number(estimateItem?.quantity) - Number(itemPoRaisedFor?.po_quantity) >
        0
      );
    } else {
      return Number(estimateItem?.quantity) > 0;
    }
  });

  const additionalChargesThatCanBeRefunded = additionalCharges.filter(
    (additionalCharge) => {
      const additionalChargeRefundRaisedFor =
        additionalChargesRefundRaisedFor.find(
          (item) => item.estimate_additional_charge_id === additionalCharge.id
        );
      if (additionalChargeRefundRaisedFor) {
        return false; // Already refunded, there is no partial refund possible
      } else {
        return true; // Not refunded yet, so can be refunded
      }
    }
  );

  const getRemainingQuantity = (estimateItemId: number) => {
    const itemPoRaisedFor = itemsPoRaisedFor.find(
      (item) => item.estimate_item_id === estimateItemId
    );
    const estimateItem = estimateItems.find(
      (item) => item.id === estimateItemId
    );
    if (itemPoRaisedFor) {
      return (
        Number(estimateItem?.quantity) - Number(itemPoRaisedFor?.po_quantity)
      );
    } else {
      return Number(estimateItem?.quantity);
    }
  };

  useEffect(() => {
    getEstimateItemPORaisedFor({ estimate_id: estimateId })
      .then((response) => {
        setItemsPoRaisedFor(response ?? []);
      })
      .catch((error) => {
        console.error("Error fetching items PO raised for:", error);
      });

    getEstimateAdditionalChargeRefundRaisedFor({ estimate_id: estimateId })
      .then((response) => {
        setAdditionalChargesRefundRaisedFor(response ?? []);
      })
      .catch((error) => {
        console.error(
          "Error fetching additional charges refund raised for:",
          error
        );
      });

    fetchPIfromId(String(estimateId))
      .then((response) => {
        if (response) {
          const estimate = response;
          setEstimateItems(estimate?.estimate_items ?? []);
          setAdditionalCharges(estimate?.estimate_additional_charges ?? []);
        }
      })
      .catch((error) => {
        console.error("Error fetching pro forma invoice:", error);
      });
  }, [estimateId]);

  const onSubmit = async () => {
    if (attachmentUrls.length === 0) {
      message.error("Please upload at least one attachment");
      return;
    }
    if (!approvalReason) {
      message.error("Please select a reason for approval");
      return;
    }
    if (showApprovalRemark && !approvalRemark) {
      message.error("Please enter a remark");
      return;
    }

    const payloadQuantityMap = Object.entries(itemQuantityMap).reduce(
      (acc, [key, value]) => {
        if (value > 0) {
          acc[Number(key)] = value;
        }
        return acc;
      },
      {} as Record<number, number>
    );
    const payload = {
      estimate_id: estimateId,
      item_quantity_mapping: payloadQuantityMap,
      additional_charges: checkedAdditionalCharges,
      attachment_urls: attachmentUrls,
      reason: showApprovalRemark ? approvalRemark : approvalReason,
    };
    try {
      const response = await raiseRefundPOForEntireEstimate(payload);
      if (response) {
        message.success("Refund request raised successfully");

        message.loading({
          content: "Finalising request...",
          key: "finalising",
        });
        const createdPoID = response?.id;
        await Promise.all(
          attachmentUrls.map(async (url: any) => {
            return attachDocumentToPO({
              po_id: createdPoID,
              attachment_url: url,
              attachment_type: "reverse_request_proof",
            });
          })
        );
        message.success({
          content: "Request submitted successfully",
          key: "finalising",
          duration: 2,
        });
        // Reset the form
        setCheckedItems([]);
        setItemQuantityMap({});
        setCheckedAdditionalCharges([]);
        setAttachmentUrls([]);
        setApprovalReason(null);
        setApprovalRemark("");
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      } else {
        message.error("Failed to raise refund request");
      }
    } catch (error) {
      console.error("Error raising refund PO:", error);
      message.error("Failed to raise refund PO. Please try again.");
      return;
    }
  };

  return (
    <div>
      <Typography.Title level={5}>Items Refund</Typography.Title>
      {itemsThatCanBeRefunded.map((item, index) => (
        <Space
          key={index}
          style={{
            width: "100%",
            marginBottom: "1rem",
            justifyContent: "space-between",
          }}
        >
          <Checkbox
            checked={
              checkedItems.includes(item.id) || itemQuantityMap[item.id] > 0
            }
            onChange={(e) => {
              const checked = e.target.checked;
              if (checked) {
                // Add item to the map
                setItemQuantityMap((prev) => {
                  const newMap = { ...prev };
                  newMap[item.id] = 1;
                  return newMap;
                });

                setCheckedItems((prev) => {
                  return [...prev, item.id];
                });
              } else {
                // Remove the item from the map
                setItemQuantityMap((prev) => {
                  const newMap = { ...prev };
                  newMap[item.id] = 0;
                  return newMap;
                });

                setCheckedItems((prev) => {
                  return prev.filter((id) => id !== item.id);
                });
              }
            }}
          >
            <Space>
              <img
                src={getImageUrl(item?.item_image || "", "50")}
                alt=""
                width={50}
                height={50}
                style={{
                  borderRadius: "5px",
                  objectFit: "contain",
                  backgroundColor: "#f0f0f0",
                }}
              />
              <div>
                {item?.boq_item}
                <div className="text-muted">
                  PI Unit Price: Rs. {item?.unit_cost_price}
                </div>
              </div>
            </Space>
          </Checkbox>
          <InputNumber
            min={0}
            max={getRemainingQuantity(item.id)}
            value={itemQuantityMap[item.id] ?? 0}
            // disabled={(itemQuantityMap[item.id] ?? 0) === 0}
            onChange={(value) => {
              setItemQuantityMap((prev) => {
                const newMap = { ...prev };
                if (value === undefined || value === null) {
                  newMap[item.id] = 0;
                } else {
                  newMap[item.id] = value;
                }
                return newMap;
              });
              if (value === 0) {
                setCheckedItems((prev) => {
                  return prev.filter((id) => id !== item.id);
                });
              } else {
                setCheckedItems((prev) => {
                  if (!prev.includes(item.id)) {
                    return [...prev, item.id];
                  }
                  return prev;
                });
              }
            }}
            style={{ width: "100px" }}
          />
        </Space>
      ))}
      <Typography.Title level={5}>Additional Charges Refund</Typography.Title>
      {additionalChargesThatCanBeRefunded.map((charge, index) => (
        <Space
          key={index}
          style={{
            width: "100%",
            marginBottom: "1rem",
            justifyContent: "space-between",
          }}
        >
          <Checkbox
            checked={checkedAdditionalCharges.includes(charge.id)}
            onChange={(e) => {
              const checked = e.target.checked;
              if (checked) {
                setCheckedAdditionalCharges((prev) => [...prev, charge.id]);
              } else {
                setCheckedAdditionalCharges((prev) =>
                  prev.filter((id) => id !== charge.id)
                );
              }
            }}
          >
            {charge?.charge_type}
          </Checkbox>
          <div>
            Rs.{" "}
            {Math.round(
              Number(charge?.amount) *
                (1 + Number(charge?.tax_value ?? 0) / 100)
            )}
          </div>
        </Space>
      ))}
      <div className="mt-4">
        <Typography.Title level={5}>Attachments</Typography.Title>
        {attachmentUrls?.map((url, index) => {
          return (
            <div
              key={index}
              style={{
                width: "100%",
                marginBottom: "0.5rem",
              }}
              className="d-flex justify-content-between align-items-center"
            >
              <a href={url} target="_blank" rel="noopener noreferrer">
                File {index + 1}
              </a>
            </div>
          );
        })}
        <input
          type="file"
          multiple
          style={{
            width: 1,
            height: 1,
            opacity: 0,
            position: "absolute",
            zIndex: -1,
            pointerEvents: "none",
          }}
          onChange={async (e) => {
            const files = e.target.files;
            if (files) {
              message.loading({
                content: "Uploading files...",
                key: "uploading",
              });
              const urls = await Promise.all(
                Array.from(files).map(async (file) => {
                  const formData = new FormData();
                  formData.append("file", file);
                  return uploadImage(formData);
                })
              );
              setAttachmentUrls((prev) => {
                return [...prev, ...urls];
              });

              message.success({
                content: "Files uploaded successfully",
                key: "uploading",
                duration: 2,
              });
            }
          }}
          ref={fileInputRef}
        />
        <Button
          type="primary"
          onClick={() => {
            if (fileInputRef.current) {
              fileInputRef.current.click();
            }
          }}
          style={{ marginTop: "1rem" }}
        >
          Upload Attachments
        </Button>
      </div>
      <Divider />

      <div className="my-3">
        <Select
          className="w-100"
          placeholder="Select reason"
          onChange={(value) => {
            setApprovalReason(value);
          }}
          value={approvalReason}
        >
          {ApprovalReasons.map((reason) => {
            return (
              <Select.Option key={reason} value={reason}>
                {reason}
              </Select.Option>
            );
          })}
        </Select>
        {showApprovalRemark && (
          <div className="mt-2">
            <Input.TextArea
              placeholder="Enter remark"
              onChange={(e) => {
                setApprovalRemark(e.target.value);
              }}
              value={approvalRemark}
              rows={4}
            />
          </div>
        )}
      </div>
      <div className="d-flex justify-content-end mt-5">
        <Button
          type="primary"
          onClick={() => {
            // Handle the refund submission logic here
            onSubmit();
          }}
        >
          Submit Refund
        </Button>
      </div>
    </div>
  );
}
